export interface AtdOrdersListKeyParams {
  readonly accountID: string;
  readonly product: "live" | "paper";
  readonly symbols: readonly string[];
  readonly status?: string;
  readonly side?: string;
  readonly after: string;
  readonly until?: string;
  readonly assetClass?: string;
  readonly orderType?: string;
  readonly direction: "asc" | "desc";
  readonly pageSize: number;
  readonly requestLimit: number;
  readonly afterOrderID?: string;
  readonly beforeOrderID?: string;
}

export interface PortfolioHistoryKeyParams {
  readonly accountID: string;
  readonly product?: string;
  readonly period?: string;
  readonly intradayReporting?: string;
  readonly start?: string;
  readonly end?: string;
  readonly pnlReset?: string;
  readonly timeframe?: string;
}

/**
 * Centralized query key factory for react-query cache management.
 *
 * Every useQuery/useRequest hook should reference keys from this factory
 * instead of ad-hoc strings or arrays. Keys are structured from generic
 * to specific so that prefix-based invalidation works correctly:
 *
 *   queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })    // all orders
 *   queryClient.invalidateQueries({ queryKey: queryKeys.positions.all }) // all positions
 *
 * RULE: Every child key MUST spread its parent's `.all` array so that
 * prefix-based invalidation always works. Never use a flat string that
 * diverges from the parent prefix.
 *
 * See: https://tkdodo.eu/blog/effective-react-query-keys
 */

export const queryKeys = {
  // ---------------------------------------------------------------------------
  // Accounts
  // ---------------------------------------------------------------------------
  accounts: {
    all: ["accounts"] as const,
    live: () => [...queryKeys.accounts.all, "live"] as const,
    paper: () => [...queryKeys.accounts.all, "paper"] as const,
    paperDetail: (ids: string[]) =>
      [...queryKeys.accounts.paper(), "detail", ...[...ids].sort()] as const,
    details: (accountID: string) =>
      [...queryKeys.accounts.all, "details", accountID] as const,
    features: (accountID: string) =>
      [...queryKeys.accounts.all, "features", accountID] as const,
    featuresMulti: (ids: string[]) =>
      [...queryKeys.accounts.all, "features", "multi", ...ids.sort()] as const,
    configurations: (accountID: string, product?: string) =>
      [
        ...queryKeys.accounts.all,
        "configurations",
        accountID,
        product,
      ] as const,
    ipFilters: (accountID: string) =>
      [...queryKeys.accounts.all, "ip-filters", accountID] as const,
    alpacaStatus: () => [...queryKeys.accounts.all, "alpaca-status"] as const,
    intercomHash: (ownerID: string) =>
      [...queryKeys.accounts.all, "intercom-hash", ownerID] as const,
    accessKeys: (accountID: string, product?: string) =>
      [...queryKeys.accounts.all, "access-keys", accountID, product] as const,
    currentAccess: (accountID: string) =>
      [...queryKeys.accounts.all, "current-access", accountID] as const,
    accessRoles: {
      all: (accountID: string) =>
        [...queryKeys.accounts.all, "access-roles", accountID] as const,
      list: (accountID: string, params?: unknown) =>
        [
          ...queryKeys.accounts.accessRoles.all(accountID),
          "list",
          params,
        ] as const,
    },
  },

  // ---------------------------------------------------------------------------
  // Account Invites
  // ---------------------------------------------------------------------------
  accountInvites: {
    all: ["account-invites"] as const,
    account: (accountID: string) =>
      [...queryKeys.accountInvites.all, accountID] as const,
    list: (accountID: string, params?: unknown) =>
      [...queryKeys.accountInvites.account(accountID), "list", params] as const,
  },

  // ---------------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------------
  orders: {
    all: ["orders"] as const,
    atdList: (params: AtdOrdersListKeyParams) =>
      [...queryKeys.orders.all, "atd-list", params] as const,
    list: (
      accountID?: string,
      status?: string,
      side?: string,
      limit?: number,
      product?: string,
    ) =>
      [
        ...queryKeys.orders.all,
        accountID,
        status,
        side,
        limit,
        product,
      ] as const,
    detail: (accountID: string, orderId: string, product?: string) =>
      [...queryKeys.orders.all, accountID, orderId, product] as const,
  },

  // ---------------------------------------------------------------------------
  // Positions
  // ---------------------------------------------------------------------------
  positions: {
    all: ["positions"] as const,
    list: (accountID: string, product?: string) =>
      [...queryKeys.positions.all, accountID, product] as const,
    detail: (accountID: string, symbol: string, product?: string) =>
      [...queryKeys.positions.all, accountID, symbol, product] as const,
  },

  // ---------------------------------------------------------------------------
  // Balances & Margin
  // ---------------------------------------------------------------------------
  balances: {
    all: ["balances"] as const,
    list: (accountID: string, paper?: boolean) =>
      [...queryKeys.balances.all, accountID, paper] as const,
    margin: (accountID: string, paper?: boolean) =>
      [...queryKeys.balances.all, "margin", accountID, paper] as const,
    marginMulti: (accountIDs: string[], paper?: boolean) =>
      [
        ...queryKeys.balances.all,
        "margin",
        "multi",
        ...accountIDs.sort(),
        paper,
      ] as const,
    cashWithdrawable: (accountID: string) =>
      [...queryKeys.balances.all, "cash-withdrawable", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // Activities
  // ---------------------------------------------------------------------------
  activities: {
    all: ["activities"] as const,
    list: (
      accountID?: string,
      paper?: boolean,
      activityTypes?: string,
      after?: string,
      date?: string,
      until?: string,
    ) =>
      [
        ...queryKeys.activities.all,
        accountID,
        paper,
        activityTypes,
        after,
        date,
        until,
      ] as const,
    paginated: (
      accountID?: string,
      paper?: boolean,
      pageSize?: number,
      pageToken?: string,
      filters?: unknown,
    ) =>
      [
        ...queryKeys.activities.all,
        "paginated",
        accountID,
        paper,
        pageSize,
        pageToken,
        filters,
      ] as const,
  },

  // ---------------------------------------------------------------------------
  // Funding & Transfers
  // ---------------------------------------------------------------------------
  funding: {
    all: ["funding"] as const,
    achRelationships: (accountID: string) =>
      [...queryKeys.funding.all, "ach-relationships", accountID] as const,
    achTransfers: (accountID: string) =>
      [...queryKeys.funding.all, "ach-transfers", accountID] as const,
    rapydTransfers: (accountID: string) =>
      [...queryKeys.funding.all, "rapyd-transfers", accountID] as const,
    ccTransfers: (accountID: string) =>
      [...queryKeys.funding.all, "cc-transfers", accountID] as const,
    bankInstitution: (institutionId: string) =>
      [...queryKeys.funding.all, "bank-institution", institutionId] as const,
    plaidToken: (accountID: string) =>
      [...queryKeys.funding.all, "plaid-token", accountID] as const,
    plaidAuth: (plaidAccountID: string) =>
      [...queryKeys.funding.all, "plaid-auth", plaidAccountID] as const,
    relationships: (accountID: string) =>
      [...queryKeys.funding.all, "relationships", accountID] as const,
    instantAchEligibility: (accountID: string) =>
      [...queryKeys.funding.all, "instant-ach-eligibility", accountID] as const,
    allRelationships: {
      all: () => [...queryKeys.funding.all, "all-relationships"] as const,
      ach: (accountID: string) =>
        [
          ...queryKeys.funding.allRelationships.all(),
          "ach-relationships",
          accountID,
        ] as const,
      banks: (accountID: string) =>
        [
          ...queryKeys.funding.allRelationships.all(),
          "banks",
          accountID,
        ] as const,
      ccStatus: (accountID: string) =>
        [
          ...queryKeys.funding.allRelationships.all(),
          "cc-status",
          accountID,
        ] as const,
      ccAccount: (accountID: string) =>
        [
          ...queryKeys.funding.allRelationships.all(),
          "cc-account",
          accountID,
        ] as const,
    },
  },

  // ---------------------------------------------------------------------------
  // ACATS
  // ---------------------------------------------------------------------------
  acats: {
    all: ["acats"] as const,
    contraBrokers: () => [...queryKeys.acats.all, "contra-brokers"] as const,
    transfers: {
      all: (accountID: string) =>
        [...queryKeys.acats.all, "transfers", accountID] as const,
      page: (accountID: string, pageToken?: string) =>
        [...queryKeys.acats.all, "transfers", accountID, pageToken] as const,
    },
    transferDetails: (accountID: string, acatsId: string) =>
      [...queryKeys.acats.all, "transfer-details", accountID, acatsId] as const,
    transferAssets: (accountID: string, acatsId: string) =>
      [...queryKeys.acats.all, "transfer-assets", accountID, acatsId] as const,
  },

  // ---------------------------------------------------------------------------
  // Crypto
  // ---------------------------------------------------------------------------
  crypto: {
    all: ["crypto"] as const,
    assets: (accountID: string) =>
      [...queryKeys.crypto.all, "assets", accountID] as const,
    address: (accountID: string, chain: string, asset: string) =>
      [...queryKeys.crypto.all, "address", accountID, chain, asset] as const,
    transfers: (accountID: string) =>
      [...queryKeys.crypto.all, "transfers", accountID] as const,
    whitelists: (accountID: string, isApproved?: boolean) =>
      [...queryKeys.crypto.all, "whitelists", accountID, isApproved] as const,
    approvedWhitelists: (accountID: string) =>
      [...queryKeys.crypto.all, "approved-whitelists", accountID] as const,
    approvedWhitelistsBySymbol: (accountID: string, symbol: string) =>
      [...queryKeys.crypto.approvedWhitelists(accountID), symbol] as const,
    networkFee: (
      accountID: string,
      chain?: string,
      asset?: string,
      fromAddress?: string,
      toAddress?: string,
      amount?: string | number,
    ) =>
      [
        ...queryKeys.crypto.all,
        "network-fee",
        accountID,
        chain,
        asset,
        fromAddress,
        toAddress,
        amount,
      ] as const,
    vasps: (accountID: string, query: string, page: number, perPage: number) =>
      [
        ...queryKeys.crypto.all,
        "vasps",
        accountID,
        query,
        page,
        perPage,
      ] as const,
    // Page is owned by useInfiniteQuery's pageParam, so it stays out of the key.
    vaspsInfinite: (accountID: string, query: string, perPage: number) =>
      [
        ...queryKeys.crypto.all,
        "vasps-infinite",
        accountID,
        query,
        perPage,
      ] as const,
    feeStructure: () => [...queryKeys.crypto.all, "fee-structure"] as const,
    monthlyVolume: (accountID: string) =>
      [...queryKeys.crypto.all, "monthly-volume", accountID] as const,
    snapshots: (symbols: string[]) =>
      [...queryKeys.crypto.all, "snapshots", ...[...symbols].sort()] as const,
  },

  // ---------------------------------------------------------------------------
  // Currency Cloud
  // ---------------------------------------------------------------------------
  currencyCloud: {
    all: ["currency-cloud"] as const,
    account: (accountID: string) =>
      [...queryKeys.currencyCloud.all, "account", accountID] as const,
    beneficiaryRequiredDetails: (accountID: string) =>
      [
        ...queryKeys.currencyCloud.all,
        "beneficiary-required-details",
        accountID,
      ] as const,
    status: (accountID: string) =>
      [...queryKeys.currencyCloud.all, "status", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // Elite
  // ---------------------------------------------------------------------------
  elite: {
    all: ["elite"] as const,
    algoRouting: (accountID: string) =>
      [...queryKeys.elite.all, "algo-routing", accountID] as const,
    netDeposits: (accountID: string) =>
      [...queryKeys.elite.all, "net-deposits", accountID] as const,
    perks: (accountID: string) =>
      [...queryKeys.elite.all, "perks", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // FPSL (Fully Paid Securities Lending)
  // ---------------------------------------------------------------------------
  fpsl: {
    all: ["fpsl"] as const,
    tiers: (accountID: string) =>
      [...queryKeys.fpsl.all, "tiers", accountID] as const,
    status: (accountID: string) =>
      [...queryKeys.fpsl.all, "status", accountID] as const,
    analytics: (accountID: string) =>
      [...queryKeys.fpsl.all, "analytics", accountID] as const,
    loans: (
      accountID: string,
      dateRange?: unknown,
      sortOrder?: "asc" | "desc",
    ) =>
      [
        ...queryKeys.fpsl.all,
        "loans",
        accountID,
        dateRange,
        sortOrder,
      ] as const,
  },

  // ---------------------------------------------------------------------------
  // High-Yield Cash
  // ---------------------------------------------------------------------------
  highYieldCash: {
    all: ["high-yield-cash"] as const,
    status: (accountID: string) =>
      [...queryKeys.highYieldCash.all, "status", accountID] as const,
    aprTier: (accountID: string) =>
      [...queryKeys.highYieldCash.all, "apr-tier", accountID] as const,
    accruedInterest: (accountID: string) =>
      [...queryKeys.highYieldCash.all, "accrued-interest", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // Billing
  // ---------------------------------------------------------------------------
  billing: {
    all: ["billing"] as const,
    overview: (accountID?: string) =>
      [...queryKeys.billing.all, "overview", accountID] as const,
    plans: (accountID?: string) =>
      [...queryKeys.billing.all, "plans", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // IRA
  // ---------------------------------------------------------------------------
  ira: {
    all: ["ira"] as const,
    activity: (accountID?: string) =>
      [...queryKeys.ira.all, "activity", accountID] as const,
    limits: (taxYear: string) =>
      [...queryKeys.ira.all, "limits", taxYear] as const,
  },

  // ---------------------------------------------------------------------------
  // Trading & Market Data
  // ---------------------------------------------------------------------------
  assets: {
    all: ["assets"] as const,
    detail: (symbol: string) => [...queryKeys.assets.all, symbol] as const,
    list: () => [...queryKeys.assets.all, "list"] as const,
    search: (
      query: string,
      filters?: { status?: string; assetClass?: string; exchanges?: string },
      limit?: number,
    ) => [...queryKeys.assets.all, "search", query, filters, limit] as const,
    logoManifest: (baseUrl: string) =>
      [...queryKeys.assets.all, "logo-manifest", baseUrl] as const,
  },

  quotes: {
    all: ["quotes"] as const,
    batch: (symbols: string[], feed?: string) =>
      [...queryKeys.quotes.all, "batch", [...symbols].sort(), feed] as const,
    single: (symbol: string, feed?: string) =>
      [...queryKeys.quotes.all, "single", symbol, feed] as const,
    sipSnapshot: (symbol: string) =>
      [...queryKeys.quotes.all, "sip-snapshot", symbol] as const,
  },

  clock: () => ["clock"] as const,

  marketStatus: (market?: string) => ["market-status", market] as const,

  orderbooks: (symbols: string[]) =>
    ["orderbooks", [...symbols].sort()] as const,

  // ---------------------------------------------------------------------------
  // AuthX
  // ---------------------------------------------------------------------------
  authx: {
    all: ["authx"] as const,
    application: (applicationId: string) =>
      [...queryKeys.authx.all, "application", applicationId] as const,
  },

  portfolioHistory: ({
    accountID,
    product,
    period,
    intradayReporting,
    start,
    end,
    pnlReset,
    timeframe,
  }: PortfolioHistoryKeyParams) =>
    [
      "portfolio",
      accountID,
      product,
      period,
      intradayReporting,
      start,
      end,
      pnlReset,
      timeframe,
    ] as const,

  // ---------------------------------------------------------------------------
  // Options
  // ---------------------------------------------------------------------------
  options: {
    all: ["options"] as const,
    approvalStatus: (accountID: string) =>
      [...queryKeys.options.all, "approval-status", accountID] as const,
    // Contracts are public reference data served by one endpoint for every
    // account, so these keys carry no `product` — splitting the cache by it
    // would only refetch the same rows when the account lists resolve.
    expirations: (symbol?: string) =>
      [...queryKeys.options.all, "expirations", symbol] as const,
    contracts: (symbol?: string, expirationDate?: string) =>
      [...queryKeys.options.all, "contracts", symbol, expirationDate] as const,
    contractsBatch: (symbols: string[]) =>
      [...queryKeys.options.all, "contracts-batch", ...symbols] as const,
    chainSnapshots: (symbol?: string, expirationDate?: string) =>
      [
        ...queryKeys.options.all,
        "chain-snapshots",
        symbol,
        expirationDate,
      ] as const,
    // One request spanning the expirations on screen, rather than one each — see
    // `useGetOptionsData`.
    chainSnapshotsRange: (
      symbol?: string,
      expirationDateGte?: string,
      expirationDateLte?: string,
    ) =>
      [
        ...queryKeys.options.all,
        "chain-snapshots-range",
        symbol,
        expirationDateGte,
        expirationDateLte,
      ] as const,
  },

  // ---------------------------------------------------------------------------
  // OAuth
  // ---------------------------------------------------------------------------
  oauth: {
    all: ["oauth"] as const,
    clientLogo: (clientId: string) =>
      [...queryKeys.oauth.all, "client-logo", clientId] as const,
    clientById: (clientId: string) =>
      [...queryKeys.oauth.all, "client-by-id", clientId] as const,
    connectedClients: (accountID: string) =>
      [...queryKeys.oauth.all, "clients-connected", accountID] as const,
    createdClients: () => [...queryKeys.oauth.all, "clients-created"] as const,
    tokens: (accountID: string) =>
      [...queryKeys.oauth.all, "tokens", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // Documents & Onboarding
  // ---------------------------------------------------------------------------
  documents: {
    all: ["documents"] as const,
    requests: (accountID: string) =>
      [...queryKeys.documents.all, "requests", accountID] as const,
    uploads: (accountID: string) =>
      [...queryKeys.documents.all, "uploads", accountID] as const,
    ownerDocuments: (accountID: string, ownerId: string) =>
      [...queryKeys.documents.all, "owner", accountID, ownerId] as const,
    owners: (accountID: string) =>
      [...queryKeys.documents.all, "owners", accountID] as const,
    onfidoSdkToken: (accountID: string) =>
      [...queryKeys.documents.all, "onfido-sdk-token", accountID] as const,
    entityTaxForm: (accountID: string, ownerId: string, taxForm?: string) =>
      [
        ...queryKeys.documents.all,
        "entity-tax-form",
        accountID,
        ownerId,
        taxForm,
      ] as const,
    entityDocs: (
      docType: string,
      docSubType: string,
      accountID: string,
      ownerId: string,
    ) =>
      [
        ...queryKeys.documents.all,
        "entity-docs",
        docType,
        docSubType,
        accountID,
        ownerId,
      ] as const,
    nonIdentity: (accountID: string) =>
      [...queryKeys.documents.all, "non-identity", accountID] as const,
  },

  // ---------------------------------------------------------------------------
  // Countries & Geo
  // ---------------------------------------------------------------------------
  countries: () => ["countries"] as const,
  countryInfos: () => ["country-infos"] as const,
  ipAddress: () => ["ip-address"] as const,

  // ---------------------------------------------------------------------------
  // Watchlists
  // ---------------------------------------------------------------------------
  watchlists: {
    all: ["watchlists"] as const,
    list: (accountID: string) =>
      [...queryKeys.watchlists.all, accountID] as const,
    detail: (accountID: string, name: string) =>
      [...queryKeys.watchlists.all, accountID, name] as const,
  },

  // ---------------------------------------------------------------------------
  // Trusted Contact
  // ---------------------------------------------------------------------------
  trustedContact: (accountID: string) =>
    ["trusted-contact", accountID] as const,

  // ---------------------------------------------------------------------------
  // Market Data Subscriptions
  // ---------------------------------------------------------------------------
  marketDataSubscription: () => ["market-data-subscription"] as const,

  // ---------------------------------------------------------------------------
  // Agreements
  // ---------------------------------------------------------------------------
  agreements: {
    all: ["agreements"] as const,
    preview: (accountID: string, agreement: string) =>
      [...queryKeys.agreements.all, "preview", accountID, agreement] as const,
  },
} as const;
